// 현장 값 타입·행 — 목록은 `0024_project.sql` 의 check 와 한 글자도 다르면 안 된다(migrations 테스트가 맞춰 본다).

import type { OrgScopedRow } from "../common/types";

export const PROJECT_STATUSES = ["SCHEDULED", "IN_PROGRESS", "WARRANTY", "CLOSED"] as const;
export type ProjectStatus = (typeof PROJECT_STATUSES)[number];

export const PROJECT_STATUS_LABEL: Record<ProjectStatus, string> = {
  SCHEDULED: "시공대기",
  IN_PROGRESS: "시공중",
  WARRANTY: "유지보수",
  CLOSED: "완료",
};

// 준공(유지보수·완료) 현장 — 후기 받기(`0044` customer_review_guard)와 공개 링크 「완료 현장」 수가 같이 쓴다.
export const FINISHED_PROJECT_STATUSES = ["WARRANTY", "CLOSED"] as const satisfies readonly ProjectStatus[];

export const PROJECT_PHASE_STATUSES = ["PENDING", "ACTIVE", "DONE", "HOLD"] as const;
export type ProjectPhaseStatus = (typeof PROJECT_PHASE_STATUSES)[number];

export const PROJECT_PHASE_STATUS_LABEL: Record<ProjectPhaseStatus, string> = {
  PENDING: "대기",
  ACTIVE: "진행",
  DONE: "완료",
  HOLD: "보류",
};

// 현장일지 구분 — `0067` 의 project_log.kind check 와 같은 목록(무상 A/S 는 유지보수 현장만).
export const PROJECT_LOG_KINDS = ["NOTE", "ISSUE", "NO_SHOW", "EXTRA_WORK", "WARRANTY_AS"] as const;

// 일지 날씨 글자 수 — `0128` project_log_weather_len(btrim 1~40)과 같은 자.
export const MAX_LOG_WEATHER = 40;
export type ProjectLogKind = (typeof PROJECT_LOG_KINDS)[number];

export const PROJECT_LOG_KIND_LABEL: Record<ProjectLogKind, string> = {
  NOTE: "메모",
  ISSUE: "문제",
  NO_SHOW: "작업자 노쇼",
  EXTRA_WORK: "추가공정",
  WARRANTY_AS: "무상 A/S",
};

// 현장 사진 형식 — `0025` 의 project_photo.mime_type check 와 같은 목록.
export const PHOTO_MIME_TYPES = ["image/jpeg", "image/png", "image/webp"] as const;
export type PhotoMimeType = (typeof PHOTO_MIME_TYPES)[number];

export interface Project extends OrgScopedRow {
  contract_id: string;
  customer_id: string;
  assignee_id: string | null;
  created_by: string | null;
  rev: number;
  title: string;
  site_address: string;
  status: ProjectStatus;
  planned_start_date: string | null;
  planned_end_date: string | null;
  started_at: string | null;
  work_saturday: boolean;
  work_sunday: boolean;
  work_holiday: boolean;
  // null = 공휴일 미반영 · [] = 반영했고 기간 안에 없음.
  schedule_holidays: string[] | null;
  // 준공 — `0027`. 유지보수 종료일은 유지보수를 거친 현장만, 닫은 시각은 CLOSED 만.
  completion_date: string | null;
  warranty_end_date: string | null;
  closed_at: string | null;
  // 고객 앱 연결 코드(`0132`) — 직원이 만들 때만 생긴다.
  connect_code?: string | null;
}

// 준공 뒤 갈 곳 — 유지보수 기간을 두거나 바로 완료.
export const PROJECT_COMPLETE_NEXT = ["WARRANTY", "CLOSED"] as const;
export type ProjectCompleteNext = (typeof PROJECT_COMPLETE_NEXT)[number];

export interface ProjectCompleteInput {
  completion_date: string;
  next: ProjectCompleteNext;
  base_rev: number;
  warranty_months: number;
}

// 유지보수 종료일 변경 — `0030`.
export interface ProjectWarrantyEndInput {
  warranty_end_date: string;
  base_rev: number;
}

export interface ProjectPhase extends OrgScopedRow {
  project_id: string;
  phase_id: string;
  phase_code: string;
  duration_days: number;
  start_date: string | null;
  end_date: string | null;
  // 계획 대비 실적 — `0066`. 최초 계획은 현장을 만들 때 한 번 고정 · 실제 날짜는 상태 변경이 KST 오늘로 쓴다.
  baseline_start_date: string | null;
  baseline_end_date: string | null;
  actual_start_date: string | null;
  actual_end_date: string | null;
  schedule_reason: string;
  status: ProjectPhaseStatus;
  sort_order: number;
  note: string;
}

export interface ProjectDetail {
  project: Project;
  phases: ProjectPhase[];
}

export interface ProjectLog extends OrgScopedRow {
  project_id: string;
  log_date: string;
  kind: ProjectLogKind;
  body: string;
  author_id: string | null;
  // 실적(#383) — 쌓기만 · 실제 일수는 같은 공정에 일지가 달린 날짜 수로 나중에 센다.
  project_phase_id: string | null;
  crew_count: number | null;
  // 날씨(`0128`) — 담당자가 손으로 적는 짧은 글 · 옛 행·안 적은 일지는 null.
  weather?: string | null;
}

export interface ProjectPhoto extends OrgScopedRow {
  project_id: string;
  project_phase_id: string | null;
  log_date: string;
  storage_path: string;
  mime_type: PhotoMimeType;
  size_bytes: number;
  caption: string;
  uploaded_by: string | null;
}

// 목록 날짜 범위(YYYY-MM-DD, 양끝 포함).
export interface DateRange {
  from?: string;
  to?: string;
}

export interface ProjectLogInput {
  id: string;
  log_date: string;
  kind: ProjectLogKind;
  body: string;
  project_phase_id?: string | null;
  crew_count?: number | null;
  weather?: string | null;
}

export interface ProjectPhotoInsert {
  id: string;
  log_date: string;
  storage_path: string;
  mime_type: PhotoMimeType;
  size_bytes: number;
  caption: string;
  project_phase_id: string | null;
}
